import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutGrid,
  MessageSquare,
  Search,
  Library,
  PlusCircle,
  Users,
  Upload,
  Sparkles,
  Clock,
  CheckCircle,
  Edit,
  AlertCircle,
  ChevronDown,
  FileText,
  Shield,
  ShieldCheck,
  GraduationCap,
  Settings,
  LogOut,
  Palette,
  Sun,
  Moon,
  Building2,
  ClipboardList,
  UserCheck,
  Vote,
  Calendar,
  Zap,
  BarChart3,
  CreditCard,
  CheckSquare,
  Network,
  FileEdit,
  ShieldAlert,
  LifeBuoy,
  BookOpen,
  FolderOpen,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getInitials } from "@/lib/avatar-utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth, UserRole } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.png";
import { Skeleton } from "@/components/ui/skeleton";
import { useSubscription } from "@/hooks/use-subscription";

const mainItems = [
  { title: "Home", url: "/home", icon: LayoutGrid, roles: [] as UserRole[] },
  { title: "Dashboard", url: "/dashboard", icon: BarChart3, roles: [] as UserRole[] },
  { title: "Policy Library", url: "/policy-library", icon: Library, roles: [] as UserRole[] },
  { title: "Knowledge Base", url: "/knowledge-base", icon: BookOpen, roles: [] as UserRole[] },
  { title: "Quizzes", url: "/quizzes", icon: GraduationCap, roles: [] as UserRole[] },
  { title: "AI Agent", url: "/ai-agent", icon: Sparkles, roles: [] as UserRole[] },
];

const workflowItems = [
  { title: "Draft Policies", url: "/workflow/draft", icon: Edit, status: 'draft', roles: ['creator', 'admin'] as UserRole[] },
  { title: "Under Review", url: "/workflow/review", icon: Clock, status: 'under_review', roles: ['creator', 'reviewer', 'admin'] as UserRole[] },
  { title: "Pending Approval", url: "/workflow/pending_approval", icon: Clock, status: 'pending_approval', roles: ['creator', 'reviewer', 'approver', 'admin'] as UserRole[] },
  { title: "Approved", url: "/workflow/approved", icon: CheckCircle, status: 'approved', roles: [] as UserRole[] },
  { title: "Rejected", url: "/workflow/rejected", icon: AlertCircle, status: 'rejected', roles: ['creator', 'admin'] as UserRole[] },
  { title: "Expired & Renewals", url: "/workflow/expired-renewals", icon: AlertCircle, status: 'expired-renewals', roles: [] as UserRole[] },
];

const complianceItems = [
  { title: "Programs", url: "/compliance/programs", icon: ShieldCheck, roles: [] as UserRole[] },
  { title: "Metrics", url: "/compliance", icon: BarChart3, roles: [] as UserRole[] },
  { title: "Tasks", url: "/compliance/tasks", icon: CheckSquare, roles: [] as UserRole[] },
  { title: "Evidence", url: "/compliance/evidence", icon: FolderOpen, roles: [] as UserRole[] },
  { title: "Auditor", url: "/compliance/auditor", icon: UserCheck, roles: [] as UserRole[] },
];

const riskItems = [
  { title: "Overview", url: "/risk/overview", icon: LayoutGrid, roles: [] as UserRole[] },
  { title: "Risk Register", url: "/risk/register", icon: ClipboardList, roles: [] as UserRole[] },
  { title: "Heat Map", url: "/risk/heat-map", icon: BarChart3, roles: [] as UserRole[] },
  { title: "Risk Appetite", url: "/risk/appetite", icon: ShieldCheck, roles: [] as UserRole[] },
];

const governanceItems = [
  { title: "Governance Structure", url: "/governance/dashboard", icon: LayoutGrid, roles: [] as UserRole[], comingSoon: false },
  { title: "Org Chart", url: "/governance/org-chart", icon: Network, roles: [] as UserRole[], comingSoon: false },
  { title: "Terms of Reference", url: "/governance/tor", icon: ClipboardList, roles: [] as UserRole[], comingSoon: false },
  { title: "Voting & Decisions", url: "/governance/voting", icon: Vote, roles: [] as UserRole[], comingSoon: false },
  { title: "Meeting Minutes", url: "/governance/meetings", icon: Calendar, roles: [] as UserRole[], comingSoon: false },
  { title: "Task Automation", url: "/governance/automation", icon: Zap, roles: [] as UserRole[], comingSoon: false },
];

const adminItems = [
  { title: "Billing", url: "/billing", icon: CreditCard },
  { title: "Team Management", url: "/team", icon: Users },
  { title: "Audit Trail", url: "/activity-log", icon: FileText },
];

interface AppSidebarProps {
  onOpenCommand: () => void;
}

export function AppSidebar({ onOpenCommand }: AppSidebarProps) {
  const { state, isMobile, toggleSidebar } = useSidebar();
  const { hasAnyRole, profile, company, signOut, roles } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const isCollapsed = state === "collapsed";
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const [governanceOpen, setGovernanceOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [policyCounts, setPolicyCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const { subscription, loading: subLoading } = useSubscription(company?.id);

  // Smart auto-collapse: expand sections based on active route
  useEffect(() => {
    if (isCollapsed) {
      setWorkflowOpen(false);
      setComplianceOpen(false);
      setGovernanceOpen(false);
      setAdminOpen(false);
    } else {
      // Auto-expand based on current route
      const onWorkflowPage = currentPath.startsWith('/workflow');
      const onCompliancePage = currentPath.startsWith('/compliance');
      const onRiskPage = currentPath.startsWith('/risk');
      const onGovernancePage = currentPath.startsWith('/governance');
      const onAdminPage = adminItems.some((item) => currentPath === item.url);

      setWorkflowOpen(onWorkflowPage);
      setComplianceOpen(onCompliancePage);
      setRiskOpen(onRiskPage);
      setGovernanceOpen(onGovernancePage);
      setAdminOpen(onAdminPage);
    }
  }, [isCollapsed, currentPath]);

  // Fetch policy counts
  useEffect(() => {
    if (profile?.company_id) {
      fetchPolicyCounts();
    }
  }, [profile?.company_id]);

  const fetchPolicyCounts = async () => {
    if (!profile?.company_id) return;

    try {
      const { data: policies } = await supabase
        .from('policies')
        .select('status')
        .eq('company_id', profile.company_id);

      if (policies) {
        const counts: Record<string, number> = {};
        workflowItems.forEach(item => {
          counts[item.status] = policies.filter(p => p.status === item.status).length;
        });
        setPolicyCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching policy counts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on user roles
  const filteredMainItems = mainItems.filter(item =>
    item.roles.length === 0 || hasAnyRole(item.roles)
  );

  const filteredWorkflowItems = workflowItems.filter(item =>
    item.roles.length === 0 || hasAnyRole(item.roles)
  );

  const filteredComplianceItems = complianceItems.filter(item =>
    item.roles.length === 0 || hasAnyRole(item.roles)
  );

  const isActive = (path: string) => currentPath === path || (path !== "/compliance" && currentPath.startsWith(`${path}/`));
  const isWorkflowActive = filteredWorkflowItems.some(item => isActive(item.url));
  const isComplianceActive = filteredComplianceItems.some(item => isActive(item.url));
  const isRiskActive = riskItems.some(item => isActive(item.url));
  const isExpanded = filteredMainItems.some((i) => isActive(i.url)) || isWorkflowActive || isComplianceActive || isRiskActive;

  const getNavCls = ({ isActive: activeState }: { isActive: boolean }) => {
    // Data attribute for SidebarMenuButton to pick up active state
    return activeState
      ? "bg-primary/15 text-foreground font-medium"
      : "text-muted-foreground hover:bg-primary/10 hover:text-foreground";
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const getPrimaryRole = () => {
    if (roles.includes('admin')) return 'Admin';
    if (roles.includes('approver')) return 'Approver';
    if (roles.includes('reviewer')) return 'Reviewer';
    if (roles.includes('creator')) return 'Creator';
    return 'User';
  };

  return (
    <Sidebar
      className="border-r"
      collapsible="icon"
    >
      <SidebarContent className="bg-sidebar flex flex-col">
        {/* Fixed Header - Logo and Brand */}
        <div className="flex-shrink-0 flex items-center border-b px-4 py-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {!isCollapsed && (
              <div className="flex-shrink-0">
                <img src={logo} alt="Manageaze" className="w-8 h-8" />
              </div>
            )}
            <div className={`transition-all duration-200 ease-in-out overflow-hidden ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
              }`}>
              {!company?.name && loading ? (
                <div className="space-y-1">
                  <Skeleton variant="shimmer" className="h-5 w-32" />
                  <Skeleton variant="shimmer" className="h-3 w-28" />
                </div>
              ) : (
                <>
                  <h2 className="font-semibold text-lg truncate">{company?.name || 'Manageaze'}</h2>
                  <p className="text-xs text-muted-foreground truncate">Powered by Manageaze</p>
                  <div className="mt-1 mb-2">
                    {subLoading ? (
                      <Skeleton variant="shimmer" className="h-3 w-24" />
                    ) : subscription?.plan_type ? (
                      <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {subscription.plan_type === 'trial' && 'Trial Plan'}
                        {subscription.plan_type === 'pro' && 'Pro Plan'}
                        {subscription.plan_type === 'enterprise' && 'Enterprise Plan'}
                      </span>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
          {!isMobile && (
            <SidebarTrigger className="flex-shrink-0 text-muted-foreground hover:text-foreground" />
          )}
        </div>

        {/* Edge resize rail for collapsed/expanded toggle */}
        {!isMobile && <SidebarRail />}

        {!isCollapsed && (
          <div className="flex-shrink-0 px-3 py-3">
            <button
              type="button"
              onClick={onOpenCommand}
              className="group flex h-11 w-full items-center gap-3 rounded-xl border border-transparent bg-muted/70 px-3 text-left text-muted-foreground shadow-inner transition-all duration-200 hover:border-border hover:bg-background hover:text-foreground hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Search className="h-4 w-4 shrink-0 transition-colors group-hover:text-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">Search</span>
              <span className="flex h-7 min-w-7 items-center justify-center rounded-lg bg-background px-2 text-sm font-medium text-muted-foreground shadow-sm ring-1 ring-border">
                /
              </span>
            </button>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto py-2 sidebar-scroll">

          {/* Create Policy Split Button - Above Main Navigation */}
          {loading ? (
            <div className="px-2 mb-3">
              <div className="flex gap-0">
                <Skeleton variant="shimmer" className="flex-1 h-10 rounded-r-none" />
                <Skeleton variant="shimmer" className="h-10 w-10 rounded-l-none" />
              </div>
            </div>
          ) : hasAnyRole(['creator', 'admin']) && (
            <div className="px-2 mb-3">
              <div className="flex">
                {/* Main button part - navigates to /begin */}
                <Button
                  asChild
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-r-none"
                >
                  <NavLink to="/begin" className="flex items-center gap-2 justify-center">
                    <PlusCircle className="h-4 w-4" />
                    {!isCollapsed && <span className="font-medium">Create Policy</span>}
                  </NavLink>
                </Button>

                {/* Dropdown part - opens menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-l-none px-2 border-l border-primary-foreground/20"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => navigate('/policy-creator')}>
                      <FileEdit className="h-4 w-4 mr-2" />
                      Blank Policy
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/ai-agent')}>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Create Policy Using AI
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/templates')}>
                      <LayoutGrid className="h-4 w-4 mr-2" />
                      From Template
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/upload-policy')}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Policy
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}

          {/* Main Navigation */}
          <SidebarGroup>
            <SidebarGroupLabel className={isCollapsed ? "sr-only" : ""}>
              Main Navigation
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <AnimatePresence>
                  {filteredMainItems.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ delay: 0.1 * index, duration: 0.3 }}
                    >
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          asChild
                          tooltip={isCollapsed ? item.title : undefined}
                          className="transition-all duration-200"
                          data-active={isActive(item.url) || undefined}
                        >
                          <NavLink to={item.url} end className={getNavCls}>
                            <item.icon className="h-4 w-4" />
                            <span className={isCollapsed ? "sr-only" : ""}>{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Policy Workflows */}
          {filteredWorkflowItems.length > 0 && !isCollapsed && (
            <SidebarGroup>
              <Collapsible open={workflowOpen} onOpenChange={setWorkflowOpen}>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={isCollapsed ? "Policy Workflow" : undefined}
                    className="w-full justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span className={isCollapsed ? "sr-only" : ""}>Policy Workflow</span>
                    </div>
                    {!isCollapsed && (
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${workflowOpen ? 'rotate-180' : ''
                          }`}
                      />
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <AnimatePresence>
                        {filteredWorkflowItems.map((item, index) => (
                          <motion.div
                            key={item.title}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ delay: 0.1 * index, duration: 0.3 }}
                          >
                            <SidebarMenuItem>
                              <SidebarMenuButton
                                asChild
                                className="transition-all duration-200 pl-6"
                                data-active={isActive(item.url) || undefined}
                              >
                                <NavLink to={item.url} className={getNavCls}>
                                  <item.icon className="h-4 w-4" />
                                  <div className="flex items-center justify-between w-full">
                                    <span>{item.title}</span>
                                    {loading ? (
                                      <Skeleton variant="shimmer" className="h-4 w-6 rounded" />
                                    ) : (
                                      <span className="bg-muted-foreground/20 text-xs px-1.5 py-0.5 rounded">
                                        {policyCounts[item.status] || 0}
                                      </span>
                                    )}
                                  </div>
                                </NavLink>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          )}

          {/* Governance Section */}
          {!isCollapsed && (
            <SidebarGroup>
              <Collapsible open={governanceOpen} onOpenChange={setGovernanceOpen}>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={isCollapsed ? "Governance Workflow" : undefined}
                    className="w-full justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      <span className={isCollapsed ? "sr-only" : ""}>Governance</span>
                    </div>
                    {!isCollapsed && (
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${governanceOpen ? 'rotate-180' : ''
                          }`}
                      />
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <AnimatePresence>
                        {governanceItems.map((item, index) => {
                          const Icon = item.icon;
                          const isItemActive = currentPath === item.url;

                          if (item.comingSoon) {
                            return (
                              <motion.div
                                key={item.title}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ delay: 0.1 * index, duration: 0.3 }}
                              >
                                <SidebarMenuItem>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <SidebarMenuButton
                                          disabled
                                          className={`w-full opacity-50 cursor-not-allowed transition-all duration-200 pl-6 ${isItemActive ? 'bg-accent' : ''
                                            }`}
                                        >
                                          <Icon className="h-4 w-4" />
                                          <span className={isCollapsed ? "sr-only" : ""}>{item.title}</span>
                                        </SidebarMenuButton>
                                      </TooltipTrigger>
                                      <TooltipContent side="right">
                                        <p>Coming Soon</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </SidebarMenuItem>
                              </motion.div>
                            );
                          }

                          return (
                            <motion.div
                              key={item.title}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -20 }}
                              transition={{ delay: 0.1 * index, duration: 0.3 }}
                            >
                              <SidebarMenuItem>
                                <SidebarMenuButton
                                  asChild
                                  className="transition-all duration-200 pl-6"
                                  data-active={isItemActive || undefined}
                                >
                                  <NavLink to={item.url} className={getNavCls}>
                                    <Icon className="h-4 w-4" />
                                    <span className={isCollapsed ? "sr-only" : ""}>{item.title}</span>
                                  </NavLink>
                                </SidebarMenuButton>
                              </SidebarMenuItem>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          )}

          {/* Compliance Section */}
          {filteredComplianceItems.length > 0 && !isCollapsed && (
            <SidebarGroup>
              <Collapsible open={complianceOpen} onOpenChange={setComplianceOpen}>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={isCollapsed ? "Compliance" : undefined}
                    className="w-full justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      <span className={isCollapsed ? "sr-only" : ""}>Compliance</span>
                    </div>
                    {!isCollapsed && (
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${complianceOpen ? 'rotate-180' : ''
                          }`}
                      />
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <AnimatePresence>
                        {filteredComplianceItems.map((item, index) => (
                          <motion.div
                            key={item.title}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ delay: 0.1 * index, duration: 0.3 }}
                          >
                            <SidebarMenuItem>
                              <SidebarMenuButton
                                asChild
                                className="transition-all duration-200 pl-6"
                                data-active={isActive(item.url) || undefined}
                              >
                                <NavLink to={item.url} className={getNavCls}>
                                  <item.icon className="h-4 w-4" />
                                  <span>{item.title}</span>
                                </NavLink>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          )}

          {/* Risk Section */}
          {!isCollapsed && (
            <SidebarGroup>
              <Collapsible open={riskOpen} onOpenChange={setRiskOpen}>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={isCollapsed ? "Risk Management" : undefined}
                    className="w-full justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4" />
                      <span className={isCollapsed ? "sr-only" : ""}>Risk</span>
                    </div>
                    {!isCollapsed && (
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${riskOpen ? 'rotate-180' : ''
                          }`}
                      />
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <AnimatePresence>
                        {riskItems.map((item, index) => (
                          <motion.div
                            key={item.title}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ delay: 0.1 * index, duration: 0.3 }}
                          >
                            <SidebarMenuItem>
                              <SidebarMenuButton
                                asChild
                                className="transition-all duration-200 pl-6"
                                data-active={isActive(item.url) || undefined}
                              >
                                <NavLink to={item.url} className={getNavCls}>
                                  <item.icon className="h-4 w-4" />
                                  <span>{item.title}</span>
                                </NavLink>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          )}

          {/* Administration Section */}
          {hasAnyRole(['admin']) && !isCollapsed && (
            <SidebarGroup>
              <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    className="w-full justify-between"
                    data-active={adminItems.some((item) => isActive(item.url)) || undefined}
                  >
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      <span>Admin</span>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${adminOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </SidebarMenuButton>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {adminItems.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton
                            asChild
                            className="transition-all duration-200 pl-6"
                            data-active={isActive(item.url) || undefined}
                          >
                            <NavLink to={item.url} className={getNavCls}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          )}

        </div>

        {/* Need Help? — Consulting link */}
        <div className="flex-shrink-0 border-t">
          <div className="px-2 py-2">
            <SidebarMenuButton
              asChild
              tooltip={isCollapsed ? "Need Help?" : undefined}
              className="w-full transition-all duration-200"
              data-active={isActive('/consulting') || undefined}
            >
              <a
                href="/consulting"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 ${isActive('/consulting') ? 'bg-primary/15 text-foreground font-medium' : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'}`}
              >
                <LifeBuoy className="h-4 w-4 flex-shrink-0" />
                {!isCollapsed && <span>Need Help?</span>}
              </a>
            </SidebarMenuButton>
          </div>
        </div>

        {/* Fixed Footer - User Menu */}
        <div className="flex-shrink-0 border-t">
          {profile ? (
            <SidebarGroup className="p-0">
              <SidebarMenu>
                <SidebarMenuItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton
                        size="lg"
                        className={`data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground ${isCollapsed ? 'justify-center' : ''
                          }`}
                        tooltip={isCollapsed ? profile.name : undefined}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback name={profile.name}>
                            {getInitials(profile.name)}
                          </AvatarFallback>
                        </Avatar>
                        {!isCollapsed && (
                          <div className="grid flex-1 text-left text-sm leading-tight">
                            <span className="truncate font-semibold">{profile.name}</span>
                            <span className="truncate text-xs text-muted-foreground">
                              {getPrimaryRole()}
                            </span>
                          </div>
                        )}
                        {!isCollapsed && <ChevronDown className="ml-auto h-4 w-4" />}
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5">
                        <p className="text-sm font-medium">{profile.name}</p>
                        <p className="text-xs text-muted-foreground">{profile.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <NavLink to="/onboarding" className="flex items-center gap-2">
                          <GraduationCap className="h-4 w-4" />
                          Onboarding
                        </NavLink>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <NavLink to="/feedback" className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Feedback
                        </NavLink>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <NavLink to="/settings" className="flex items-center gap-2">
                          <Settings className="h-4 w-4" />
                          Settings
                        </NavLink>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          const currentTheme = localStorage.getItem('theme');
                          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                          const isDark = currentTheme === 'dark' || (!currentTheme && prefersDark);
                          const newTheme = !isDark;

                          document.documentElement.classList.toggle('dark', newTheme);
                          document.body.classList.toggle('dark', newTheme);
                          localStorage.setItem('theme', newTheme ? 'dark' : 'light');
                        }}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Palette className="h-4 w-4" />
                        <span>Toggle Theme</span>
                        <div className="ml-auto flex items-center">
                          <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                          <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="text-red-600 focus:text-red-600">
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>
          ) : loading ? (
            <SidebarGroup className="p-0">
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    size="lg"
                    className={`${isCollapsed ? 'justify-center' : ''}`}
                    disabled
                  >
                    <Skeleton variant="shimmer" className="h-8 w-8 rounded-full" />
                    {!isCollapsed && (
                      <div className="grid flex-1 text-left text-sm leading-tight space-y-1">
                        <Skeleton variant="shimmer" className="h-4 w-20" />
                        <Skeleton variant="shimmer" className="h-3 w-16" />
                      </div>
                    )}
                    {!isCollapsed && <Skeleton variant="shimmer" className="ml-auto h-4 w-4" />}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>
          ) : null}
        </div>

      </SidebarContent>
    </Sidebar>
  );
}
